import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { CacheExpiryConstants, CacheKeyConstants, CacheService, isNullOrUndefined, Permission } from '@uzurv/core';
// eslint-disable-next-line import/order
import { UZURV_REST_CONFIGURATION, UzurvRestConfiguration, UzurvRestService } from '@uzurv/http';
import { Deserialize, Serialize } from 'cerialize';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { DeactivationReason, InviteUser, Role, Tenant, User, UserRole } from '../../models';
import { UserTenancy } from '../../models/user-tenancy';

@Injectable({
  providedIn: 'root'
})
export class UserService extends UzurvRestService {
  readonly resourceUrl = 'users/';

  constructor(
    httpClient: HttpClient,
    @Inject(UZURV_REST_CONFIGURATION)
    protected uzurvRestConfiguration: UzurvRestConfiguration,
    domSanitizer: DomSanitizer,
    private cacheService: CacheService
  ) {
    super(httpClient, uzurvRestConfiguration, domSanitizer);
  }

  /**
   * Returns a user by ID
   *
   * @param userId
   * @permission user_management
   */
  findUserById(userId: number): Observable<User> {
    return this.get<User>(`${this.resourceUrl}${userId}`).pipe(map(response => Deserialize(response, User)));
  }

  /**
   * Lists users with child roles of the user in the same tenants as the provided user's ID
   *
   * @param userId
   * @param pageNumber
   * @param pageSize
   * @permission user_management
   */
  listUsers(userId: number, pageNumber: number, pageSize: number): Observable<Array<User>> {
    return this.get<Array<User>>(`${this.resourceUrl}${userId}/users/`, {
      pageNumber: pageNumber,
      pageSize: pageSize
    }).pipe(map(response => Deserialize(response, User)));
  }

  /**
   * @param searchString
   * @param pageNumber
   * @param pageSize
   * @permission user_management
   */
  searchUsers(searchString: string, pageNumber = 0, pageSize = 20): Observable<Array<User>> {
    return this.get<Array<User>>(`search/${this.resourceUrl}`, {
      searchString: searchString,
      pageNumber: pageNumber,
      pageSize: pageSize
    }).pipe(map(response => Deserialize(response, User)));
  }

  /**
   * Returns a user by username
   *
   * @param username
   * @permission user_management
   */
  findUserByUsername(username: string): Observable<User> {
    return this.get<User>(this.resourceUrl, { username: username }).pipe(map(response => Deserialize(response, User)));
  }

  /**
   * Lists a logged in users' permissions
   *
   * @param userId
   */
  listPermissionsForUser(userId: number): Observable<Array<Permission>> {
    return this.get<Array<Permission>>(`${this.resourceUrl}${userId}/permissions/`).pipe(
      map(response => Deserialize(response, Permission))
    );
  }

  /**
   * @param userId
   * @permission role_management
   */
  listChildRolesForUser(userId: number): Observable<Array<Role>> {
    return this.get<Array<Role>>(`${this.resourceUrl}${userId}/childRoles/`).pipe(map(response => Deserialize(response, Role)));
  }

  /**
   * @param userId
   * @permission user_management
   */
  listTenants(userId: number): Observable<Array<Tenant>> {
    return this.get<Array<Tenant>>(`${this.resourceUrl}${userId}/tenants/`).pipe(map(response => Deserialize(response, Tenant)));
  }

  /**
   * @param inviteUser
   * @permission user_management
   */
  inviteUser(inviteUser: InviteUser): Observable<User> {
    return this.post<InviteUser>(this.resourceUrl, inviteUser).pipe(map(response => Deserialize(response, User)));
  }

  /**
   * @param userId
   * @param tenantIds
   * @permission user_management
   */
  setUserTenancies(userId: number, tenantIds: Array<number>): Observable<Array<UserTenancy>> {
    return this.put<Array<UserTenancy>>(`${this.resourceUrl}${userId}/tenants/`, tenantIds).pipe(
      map(response => Deserialize(response, UserTenancy))
    );
  }

  /**
   * @param userId
   * @param roleIds
   * @permission user_management
   */
  setUserRoles(userId: number, roleIds: Array<number>): Observable<Array<Role>> {
    return this.put<Array<Role>>(`${this.resourceUrl}${userId}/roles/`, roleIds).pipe(map(response => Deserialize(response, Role)));
  }

  /**
   * Updates an existing user
   *
   * @param id
   * @param user
   * @permission user_management
   */
  updateUser(id: number, user: User): Observable<User> {
    return this.put<User>(`${this.resourceUrl}${id}`, user).pipe(map(response => Deserialize(response, User)));
  }

  /**
   * Returns a list of deactivation reasons
   *
   * @param overrideCache
   */
  getDeactivationReasons(overrideCache = false): Observable<Array<DeactivationReason>> {
    const cachedData = this.cacheService.getCachedData(CacheKeyConstants.DEACTIVATION_REASONS);
    if (!overrideCache && !isNullOrUndefined(cachedData)) {
      of(Deserialize(JSON.parse(cachedData), DeactivationReason));
    }

    return this.get(`${this.resourceUrl}deactivationReasons/`).pipe(
      map(response => {
        this.cacheService.cacheData(
          CacheKeyConstants.DEACTIVATION_REASONS,
          JSON.stringify(response),
          CacheExpiryConstants.DOMAIN_CACHE_EXPIRY
        );
        return Deserialize(response, DeactivationReason);
      })
    );
  }

  /**
   * Returns a deactivation reason by ID
   *
   * @param id
   * @param overrideCache
   */
  findDeactivationReasonWithId(id: number, overrideCache = false): Observable<DeactivationReason> {
    return this.getDeactivationReasons(overrideCache).pipe(
      map(response => response.find(deactivationReason => deactivationReason.id === id))
    );
  }

  /**
   * Returns a deactivation reason by name
   *
   * @param name
   * @param overrideCache
   */
  findDeactivationReasonWithName(name: string, overrideCache = false): Observable<DeactivationReason> {
    return this.getDeactivationReasons(overrideCache).pipe(
      map(response => response.find(deactivationReason => deactivationReason.name === name))
    );
  }

  /**
   * Deactivates a user with a deactivation code
   *
   * @param userId
   * @param deactivationCode
   */
  deactivateUserWithDeactivationCode(userId: number, deactivationCode: string): Observable<boolean> {
    return this.delete<boolean>(`${this.resourceUrl}${userId}?deactivationReasonCode=${deactivationCode}`);
  }

  /**
   * Reactivates a user
   *
   * @param userId
   */
  reactivateUser(userId: number): Observable<boolean> {
    return this.put<boolean>(`admin/users/${userId}/reactivate`, {});
  }

  /**
   * Returns a list of roles for a user
   *
   * @param userId
   */
  getRolesForUser(userId: number): Observable<Array<Role>> {
    return this.get<Array<Role>>(`${this.resourceUrl}${userId}/roles/`).pipe(map(response => Deserialize(response, Role)));
  }

  /**
   * Adds a role to an existing user
   *
   * @param role
   * @param userId
   */
  addRoleToUser(role: Role, userId: number): Observable<boolean> {
    const userRole: UserRole = new UserRole();
    userRole.userId = userId;
    userRole.role = role;
    userRole.active = true;

    return this.post<boolean>(`${this.resourceUrl}${userId}/roles/`, Serialize(userRole));
  }

  /**
   * Removes a role from a user
   *
   * @param role
   * @param userId
   */
  removeRoleFromUser(role: Role, userId: number): Observable<boolean> {
    return this.delete<boolean>(`${this.resourceUrl}${userId}/roles/${role.id}?deactivationReasonCode=OTHERNL`);
  }

  /**
   * Asks the server to resend email verification
   *
   * @param emailAddress
   */
  resendVerifyEmail(emailAddress: string): Observable<boolean> {
    return this.get('verifyEmail/requestVerificationToken', {
      emailAddress: emailAddress
    });
  }

  /**
   * Requests an emailed password reset token from the server to
   * a given user
   *
   * @param username
   * @deprecated
   */
  requestPasswordReset(username: string): Observable<boolean> {
    return this.get('resetPassword/requestReset', {
      username: username
    });
  }

  /**
   * Verifies a user's email using the provided token
   *
   * @param token
   */
  verifyEmail(token: string): Observable<boolean> {
    return this.get(`verifyEmail/${token}`);
  }

  /**
   * This is used when inviting WSS users
   *
   * @param token
   */
  verifyEmailAndRequestReset(token: string): Observable<string> {
    return this.get<string>(`verifyEmailAndRequestReset/${token}`);
  }

  /**
   * Validates that the supplied token is a valid password reset
   * token
   *
   * @param token
   */
  validatePasswordResetToken(token: string): Observable<boolean> {
    return this.get(`resetPassword/confirmToken/${token}`);
  }

  /**
   * Resets the users password using the supplied and confirmed
   * passwords and a token
   *
   * @param newPassword
   * @param confirmPassword
   * @param token
   */
  resetPassword(newPassword: string, confirmPassword: string, token: string): Observable<boolean> {
    const body = new HttpParams().set('newPassword', newPassword).set('confirmPassword', confirmPassword);

    return this.post(`resetPassword/${token}`, body.toString(), {
      headers: new HttpHeaders().set('Content-Type', 'application/x-www-form-urlencoded')
    });
  }

  /**
   * Lists users by clientApproverRole
   *
   * @param userId
   */
  listUsersByClientApproverRole(userId: number): Observable<Array<User>> {
    return this.get<Array<User>>(`${this.resourceUrl}${userId}/users/clientApprover`).pipe(map(response => Deserialize(response, User)));
  }

  /**
   * Lists users by providerApproverRole
   *
   * @param userId
   */
  listUsersByProviderApproverRole(userId: number): Observable<Array<User>> {
    return this.get<Array<User>>(`${this.resourceUrl}${userId}/users/providerApprover`).pipe(map(response => Deserialize(response, User)));
  }
}
